import { useQuery } from "@tanstack/react-query";
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { RichDiffAnalysis, SchemaDrift, SchemaDriftIssue, SchemaObject, SchemaTable, TraceCell, TraceItem, TraceRow, Traceability } from "../api/types";
import { DiffView } from "../components/DiffView";
import { Icon } from "../components/Icon";
import { Markdown } from "../components/Markdown";
import { Spinner } from "../components/Spinner";
import { cx } from "../lib/format";
import { itemSection, lineDiff, wordDiff, type Part, type Row } from "./textDiff";

/**
 * The rich diff viewer, shared by the agent runner's window and the
 * standalone `rich-diff report` file. Data comes from `source`.
 */
export interface RichDiffSource {
  /** A file at a commit (for the document panel); null if it does not exist there. */
  loadFile: (sha: string, path: string) => Promise<string | null>;
  /** The unified diff of the change. */
  loadPatch: () => Promise<string | null>;
}

export type RichDiffTab = "trace" | "schema" | "files";
type Tab = RichDiffTab;
/** An item (or, with no id, a whole document) to show in the document panel. */
type DocTarget = { id: string; path: string };

export function RichDiffView({
  analysis: a,
  source,
  title,
  actions,
  tab,
  onTab,
}: {
  analysis: RichDiffAnalysis;
  source: RichDiffSource;
  title: ReactNode;
  actions?: ReactNode;
  tab: Tab;
  onTab: (t: Tab) => void;
}) {
  const [doc, setDoc] = useState<DocTarget | null>(null);
  const open = (id: string) => {
    const it = a.traceability?.items[id];
    if (it) setDoc({ id, path: it.path });
  };
  const schemaChanged = a.schema ? a.schema.summary.added + a.schema.summary.removed + a.schema.summary.modified : 0;

  return (
    <div className={cx("rd-page", doc && "rd-with-doc")}>
      <header className="rd-header">
        <div className="rd-title">
          {title}
          <span className="rd-kicker">Rich diff</span>
        </div>
        <div className="rd-refs small muted">
          <code className="code-inline">{a.base.rev}</code> {short(a.base.sha)} → <code className="code-inline">{a.head.rev}</code> {short(a.head.sha)}
          <span title="The project's rich diff configuration">· {a.config_path}</span>
          {actions}
        </div>
        <nav className="tabs rd-tabs" role="tablist">
          <TabButton on={tab === "trace"} onClick={() => onTab("trace")}>
            Traceability{a.traceability ? ` · ${a.traceability.changed.length} changed` : ""}
          </TabButton>
          <TabButton on={tab === "schema"} onClick={() => onTab("schema")}>
            Database schema{a.schema ? ` · ${schemaChanged} changed` : ""}
            {a.schema?.drift?.summary.new ? ` · ${a.schema.drift.summary.new} new doc drift` : ""}
          </TabButton>
          <TabButton on={tab === "files"} onClick={() => onTab("files")}>
            Files changed · {a.files.length}
          </TabButton>
        </nav>
      </header>
      <div className="rd-body">
        <main className="rd-main">
          {a.warnings.length > 0 && (
            <ul className="rd-warnings small">
              {a.warnings.map((w) => (
                <li key={w}>
                  <Icon name="alert" size={12} /> {w}
                </li>
              ))}
            </ul>
          )}
          {tab === "trace" &&
            (a.traceability ? (
              <TraceabilityView t={a.traceability} onOpen={open} />
            ) : (
              <Empty>
                <code className="code-inline">{a.config_path}</code> defines no traceability.
              </Empty>
            ))}
          {tab === "schema" &&
            (a.schema ? (
              <SchemaView
                tables={a.schema.tables}
                summary={a.schema.summary}
                drift={a.schema.drift}
                onOpenDoc={(path) => setDoc({ id: "", path })}
              />
            ) : (
              <Empty>
                <code className="code-inline">{a.config_path}</code> defines no database schema.
              </Empty>
            ))}
          {tab === "files" && <FilesView files={a.files} source={source} />}
        </main>
        {doc && (
          <DocPanel key={`${doc.path}#${doc.id}`} a={a} source={source} target={doc} item={a.traceability?.items[doc.id]} onClose={() => setDoc(null)} />
        )}
      </div>
    </div>
  );
}

const short = (sha: string) => (sha === "WORKTREE" ? "working tree" : sha.slice(0, 8));

function TabButton({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button role="tab" aria-selected={on} className={cx("tab", on && "active")} onClick={onClick}>
      {children}
    </button>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="muted rd-empty">{children}</p>;
}

function statusLabel(s: string) {
  return s === "added" ? "Added" : s === "removed" ? "Removed" : s === "modified" ? "Changed" : "";
}

// ------------------------------------------------------------ traceability ---

function Chip({ id, item, cell, onOpen }: { id: string; item?: TraceItem; cell?: TraceCell; onOpen: (id: string) => void }) {
  const status = cell?.status ?? item?.status ?? "unchanged";
  const link = cell?.link ?? "same";
  const title = [item?.title, statusLabel(status) && `item ${statusLabel(status).toLowerCase()}`, link !== "same" && `trace link ${link}`]
    .filter(Boolean)
    .join(" · ");
  return (
    <button className={cx("rd-chip", `rd-${status}`, link !== "same" && `rd-link-${link}`)} title={title} onClick={() => onOpen(id)}>
      {link === "added" && <span aria-hidden="true">+</span>}
      {link === "removed" && <span aria-hidden="true">−</span>}
      {id}
    </button>
  );
}

const CHANGES_ONLY_KEY = "rich-diff:matrix-changes-only";

/** A matrix cell entry that changed itself or whose trace link was added or removed. */
const cellChanged = (x: TraceCell) => x.status !== "unchanged" || x.link !== "same";

/** A matrix row with a change of its own, in changed code, or in its trace links. */
function rowChanged(r: TraceRow, columns: { kind: string }[]) {
  if (r.status !== "unchanged" || r.touched_by_code) return true;
  return r.cells.some((cell, i) => cell.length > 0 && (columns[i]?.kind === "code_tests" || (cell as TraceCell[]).some(cellChanged)));
}

/** "via DREQ-1-1": the linked items a matrix cell entry is reached through. */
function Via({ ids, items, onOpen }: { ids?: string[]; items: Traceability["items"]; onOpen: (id: string) => void }) {
  if (!ids?.length) return null;
  return (
    <span className="rd-via">
      via{" "}
      {ids.map((id, i) => (
        <span key={id}>
          {i > 0 && ", "}
          <button type="button" className="rd-via-id" title={items[id]?.title} onClick={() => onOpen(id)}>
            {id}
          </button>
        </span>
      ))}
    </span>
  );
}

function TraceabilityView({ t, onOpen }: { t: Traceability; onOpen: (id: string) => void }) {
  const s = t.summary;
  const labelOf = useMemo(() => new Map(t.categories.map((c) => [c.key, c.label])), [t]);
  const changedByCat = useMemo(() => {
    const m = new Map<string, TraceItem[]>();
    for (const id of t.changed) {
      const it = t.items[id];
      if (it) m.set(it.category, [...(m.get(it.category) ?? []), it]);
    }
    const order = t.categories.map((c) => c.key);
    return [...m.entries()].sort(([x], [y]) => order.indexOf(x) - order.indexOf(y));
  }, [t]);
  const rowsLabel = t.matrix ? (labelOf.get(t.matrix.rows_category) ?? t.matrix.rows_category) : "";
  const [changesOnly, setChangesOnly] = useState(() => {
    try {
      return localStorage.getItem(CHANGES_ONLY_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggleChangesOnly = (on: boolean) => {
    setChangesOnly(on);
    try {
      localStorage.setItem(CHANGES_ONLY_KEY, on ? "1" : "0");
    } catch {
      // Not remembered; fine.
    }
  };
  const rows = t.matrix ? (changesOnly ? t.matrix.rows.filter((r) => rowChanged(r, t.matrix!.columns)) : t.matrix.rows) : [];

  return (
    <div className="rd-stack">
      <div className="rd-stats">
        <Stat n={s.requirement.changed} label="requirements changed" detail={s.requirement.impacted ? `${s.requirement.impacted} more impacted` : undefined} />
        <Stat n={s.test.changed + (s.test.test_files ?? 0)} label="tests changed" detail={`${s.test.changed} documented, ${s.test.test_files ?? 0} test files`} />
        <Stat n={s.risk.changed + s.risk.impacted} label="risks affected" detail={`${s.risk.changed} changed, ${s.risk.impacted} impacted`} tone={s.risk.changed + s.risk.impacted ? "warn" : undefined} />
        <Stat
          n={s.security_risk.changed + s.security_risk.impacted}
          label="security risks affected"
          detail={`${s.security_risk.changed} changed, ${s.security_risk.impacted} impacted`}
          tone={s.security_risk.changed + s.security_risk.impacted ? "warn" : undefined}
        />
      </div>

      {t.matrix && (
        <section>
          <h2 className="rd-h2">Trace matrix</h2>
          <p className="muted small">
            {rowsLabel} this change touches (their documents changed, or changed code names them) or impacts, as the project's rich diff
            configuration defines it. Click any ID to open it in its document.
          </p>
          <label className="checkbox rd-matrix-filter">
            <input type="checkbox" checked={changesOnly} onChange={(e) => toggleChangesOnly(e.target.checked)} /> Hide unchanged items
            {changesOnly && rows.length < t.matrix.rows.length && (
              <span className="muted small"> · {t.matrix.rows.length - rows.length} of {t.matrix.rows.length} rows hidden</span>
            )}
          </label>
          <Legend />
          {t.matrix.rows.length === 0 ? (
            <Empty>Nothing in the matrix is affected.</Empty>
          ) : rows.length === 0 ? (
            <Empty>No row has a change of its own or in its trace links; clear “Hide unchanged items” to see the impacted rows.</Empty>
          ) : (
            <div className="rd-table-wrap">
              <table className="rd-matrix">
                <thead>
                  <tr>
                    <th>{rowsLabel}</th>
                    {t.matrix.columns.map((c) => (
                      <th key={c.label}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const it = t.items[r.id];
                    return (
                      <tr key={r.id} className={cx(r.status !== "unchanged" && `rd-row-${r.status}`)}>
                        <th scope="row">
                          <Chip id={r.id} item={it} onOpen={onOpen} />
                          <div className="rd-row-title">{it?.title}</div>
                          <div className="rd-row-flags">
                            {statusLabel(r.status) && <span className={cx("rd-flag", `rd-${r.status}`)}>{statusLabel(r.status)}</span>}
                            {r.touched_by_code && (
                              <span className="rd-flag" title="Changed code names it">
                                <Icon name="terminal" size={11} /> code
                              </span>
                            )}
                          </div>
                        </th>
                        {t.matrix!.columns.map((c, i) => {
                          const cell = r.cells[i] ?? [];
                          if (!cell.length) return <td key={c.label}><span className="rd-none">—</span></td>;
                          return (
                            <td key={c.label}>
                              {c.kind === "code_tests"
                                ? (cell as { path: string; via?: string[] }[]).map((f) => (
                                    <span key={f.path} className="rd-cell-entry">
                                      <code className="rd-path" title={f.path}>
                                        {f.path.split("/").pop()}
                                      </code>
                                      <Via ids={f.via} items={t.items} onOpen={onOpen} />
                                    </span>
                                  ))
                                : (() => {
                                    const all = cell as TraceCell[];
                                    const shown = changesOnly ? all.filter(cellChanged) : all;
                                    const hidden = all.length - shown.length;
                                    return (
                                      <>
                                        {shown.map((x) => (
                                          <span key={x.id} className="rd-cell-entry">
                                            <Chip id={x.id} cell={x} item={t.items[x.id]} onOpen={onOpen} />
                                            <Via ids={x.via} items={t.items} onOpen={onOpen} />
                                          </span>
                                        ))}
                                        {hidden > 0 && (
                                          <span className="rd-hidden-n" title={all.filter((x) => !cellChanged(x)).map((x) => x.id).join(", ")}>
                                            {hidden} unchanged
                                          </span>
                                        )}
                                      </>
                                    );
                                  })()}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section>
        <h2 className="rd-h2">Changed items</h2>
        {changedByCat.length === 0 && <Empty>No traceability item changed.</Empty>}
        {changedByCat.map(([cat, items]) => (
          <div key={cat} className="rd-group">
            <h3 className="rd-h3">
              {labelOf.get(cat) ?? cat} · {items.length}
            </h3>
            {items.map((it) => (
              <ChangedItem key={it.id} it={it} onOpen={onOpen} />
            ))}
          </div>
        ))}
      </section>

      {t.impacted.length > 0 && (
        <section>
          <h2 className="rd-h2">Impacted through trace links</h2>
          <p className="muted small">Not changed themselves, but linked to something that is.</p>
          <ul className="rd-impacted">
            {t.impacted.map((imp) => (
              <li key={imp.id}>
                <Chip id={imp.id} item={t.items[imp.id]} onOpen={onOpen} />
                <span className="rd-imp-title">{t.items[imp.id]?.title}</span>
                <span className="muted small">
                  {imp.reasons.map((r, i) => (
                    <span key={i}>
                      {i > 0 && "; "}
                      {r.via} ↔ <Chip id={r.from} item={t.items[r.from]} onOpen={onOpen} />
                      {r.link !== "same" && ` (link ${r.link})`}
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {t.code_refs.length > 0 && (
        <section>
          <h2 className="rd-h2">Changed code that names items</h2>
          <ul className="rd-impacted">
            {t.code_refs.map((c) => (
              <li key={c.path}>
                {c.is_test && <span className="rd-flag">test</span>}
                <code className="rd-path">{c.path}</code>
                {c.refs.map((r) => (
                  <span key={r.id} title={`${r.change} on changed lines`}>
                    <Chip id={r.id} item={t.items[r.id]} onOpen={onOpen} />
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Stat({ n, label, detail, tone }: { n: number; label: string; detail?: string; tone?: "warn" }) {
  return (
    <div className={cx("rd-stat", tone && `rd-stat-${tone}`)}>
      <div className="rd-stat-n">{n}</div>
      <div className="rd-stat-label">{label}</div>
      {detail && <div className="muted small">{detail}</div>}
    </div>
  );
}

function Legend() {
  return (
    <div className="rd-legend small muted">
      <span className="rd-chip rd-added">Added</span>
      <span className="rd-chip rd-modified">Changed</span>
      <span className="rd-chip rd-removed">Removed</span>
      <span className="rd-chip rd-unchanged">Unchanged</span>
      <span className="rd-chip rd-unchanged rd-link-added">+ new link</span>
      <span className="rd-chip rd-unchanged rd-link-removed">− removed link</span>
    </div>
  );
}

function ChangedItem({ it, onOpen }: { it: TraceItem; onOpen: (id: string) => void }) {
  return (
    <div className={cx("rd-item", `rd-item-${it.status}`)}>
      <div className="rd-item-head">
        <Chip id={it.id} item={it} onOpen={onOpen} />
        <span className="rd-item-title">{it.title}</span>
        <span className={cx("rd-flag", `rd-${it.status}`)}>{statusLabel(it.status)}</span>
        <span className="muted small">{it.path}</span>
      </div>
      {it.changes.length > 0 && (
        <dl className="rd-changes">
          {it.changes.map((c, i) => (
            <div key={i} className="rd-change">
              <dt>{c.field}</dt>
              <dd>
                {c.link ? (
                  <>
                    {(c.added ?? []).map((id) => (
                      <span key={`a${id}`} className="rd-rel rd-added">
                        + <button className="link-button" onClick={() => onOpen(id)}>{id}</button>
                      </span>
                    ))}
                    {(c.removed ?? []).map((id) => (
                      <span key={`r${id}`} className="rd-rel rd-removed">
                        − <button className="link-button" onClick={() => onOpen(id)}>{id}</button>
                      </span>
                    ))}
                  </>
                ) : (
                  <TextDiff before={c.before ?? ""} after={c.after ?? ""} />
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ schema ---

function SchemaView({
  tables,
  summary,
  drift,
  onOpenDoc,
}: {
  tables: SchemaTable[];
  summary: Record<string, number>;
  drift: SchemaDrift | null;
  onOpenDoc: (path: string) => void;
}) {
  const [changedOnly, setChangedOnly] = useState(summary.added + summary.removed + summary.modified > 0);
  const shown = changedOnly ? tables.filter((t) => t.status !== "same") : tables;
  const unnamed = tables.filter((t) => !t.name).length;
  const byName = new Map(tables.filter((t) => t.name).map((t) => [t.name!, t.key]));
  const jump = (name: string) => document.getElementById(`tbl-${byName.get(name)}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  const docPath = drift?.documents[0] ?? tables.find((t) => t.path.endsWith(".md"))?.path;
  const driftByTable = new Map<string, SchemaDriftIssue[]>();
  for (const i of drift?.issues ?? []) driftByTable.set(i.table, [...(driftByTable.get(i.table) ?? []), i]);
  return (
    <div className="rd-stack">
      <div className="rd-stats">
        <Stat n={summary.tables} label="tables" />
        <Stat n={summary.added} label="tables added" />
        <Stat n={summary.modified} label="tables changed" tone={summary.modified ? "warn" : undefined} />
        <Stat n={summary.removed} label="tables removed" tone={summary.removed ? "warn" : undefined} />
        {drift && (
          <Stat
            n={drift.summary.issues}
            label="documentation mismatches"
            detail={`${drift.summary.new} new, ${drift.summary.resolved} resolved by this change`}
            tone={drift.summary.new ? "warn" : undefined}
          />
        )}
      </div>
      <div className="rd-toolbar">
        <label className="checkbox small">
          <input type="checkbox" checked={changedOnly} onChange={(e) => setChangedOnly(e.target.checked)} /> Changed tables only
        </label>
        {docPath && (
          <button className="btn btn-sm" onClick={() => onOpenDoc(docPath)}>
            <Icon name="file" size={13} /> Open {docPath.split("/").pop()}
          </button>
        )}
      </div>
      {unnamed > 0 && (
        <p className="muted small rd-note">
          {unnamed} table{unnamed === 1 ? " has" : "s have"} no name in {unnamed === 1 ? "its" : "their"} source and{" "}
          {unnamed === 1 ? "is" : "are"} listed by {unnamed === 1 ? "its" : "their"} first columns.
        </p>
      )}
      {drift && <DriftView drift={drift} />}
      {shown.length === 0 && <Empty>No table changed.</Empty>}
      <div className="rd-tables">
        {shown.map((t) => (
          <article key={t.key} id={`tbl-${t.key}`} className={cx("rd-tbl", `rd-tbl-${t.status}`)}>
            <header>
              <span className={cx("rd-tbl-name", !t.name && "rd-tbl-unnamed")}>{t.label}</span>
              {t.status !== "same" && <span className={cx("rd-flag", `rd-${t.status}`)}>{statusLabel(t.status)}</span>}
              {driftByTable.has(t.label) && (
                <span
                  className={cx("rd-flag", "rd-drift", driftByTable.get(t.label)!.some((i) => i.new) && "rd-drift-new")}
                  title={driftByTable.get(t.label)!.map(driftText).join("\n")}
                >
                  <Icon name="alert" size={11} /> docs
                </span>
              )}
            </header>
            {t.description && <p className="rd-tbl-desc">{t.description}</p>}
            {t.description_before && <p className="rd-tbl-desc rd-before">Was: {t.description_before}</p>}
            <ul className="rd-cols">
              {t.columns.map((c) => (
                <li key={c.name} className={cx("rd-col", `rd-col-${c.status}`)} title={c.notes || undefined}>
                  <span className="rd-col-mark" aria-hidden="true">
                    {c.status === "added" ? "+" : c.status === "removed" ? "−" : c.status === "modified" ? "~" : ""}
                  </span>
                  <span className="rd-col-name">{c.name}</span>
                  <span className="rd-col-type">{c.type}</span>
                  {c.references && (
                    <button
                      className="rd-ref link-button"
                      onClick={() => jump(c.references!)}
                      disabled={!byName.has(c.references)}
                      title={byName.has(c.references) ? `Go to ${c.references}` : `References ${c.references}`}
                    >
                      → {c.references}
                    </button>
                  )}
                  {c.values.length > 0 && <span className="rd-col-values">{c.values.join(" · ")}</span>}
                  {c.changes.length > 0 && (
                    <ul className="rd-col-changes">
                      {c.changes.map((ch, i) => (
                        <li key={i}>
                          {ch.field === "values" ? (
                            <>
                              values {(ch.added ?? []).map((v) => <span key={v} className="rd-added"> +{v}</span>)}
                              {(ch.removed ?? []).map((v) => <span key={v} className="rd-removed"> −{v}</span>)}
                            </>
                          ) : (
                            <>
                              {ch.field}: <span className="rd-removed">{ch.before || "(empty)"}</span> → <span className="rd-added">{ch.after || "(empty)"}</span>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
            <SchemaObjects title="Indexes" objects={t.indexes ?? []} />
            <SchemaObjects title="Triggers" objects={t.triggers ?? []} />
          </article>
        ))}
      </div>
    </div>
  );
}

/** A table's indexes or triggers, each with its definition (a changed one as a diff). */
function SchemaObjects({ title, objects }: { title: string; objects: SchemaObject[] }) {
  if (!objects.length) return null;
  return (
    <>
      <h4 className="rd-objs-title">{title}</h4>
      <ul className="rd-cols">
        {objects.map((o) => (
          <li key={o.name} className={cx("rd-col", "rd-obj", `rd-col-${o.status}`)}>
            <span className="rd-col-mark" aria-hidden="true">
              {o.status === "added" ? "+" : o.status === "removed" ? "−" : o.status === "modified" ? "~" : ""}
            </span>
            <span className="rd-col-name">{o.name}</span>
            <span className="rd-obj-def">
              {o.status === "modified" && o.before != null ? <TextDiff before={o.before} after={o.definition} /> : o.definition}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

/** One mismatch between the documented and the actual schema, as plain text. */
function driftText(i: SchemaDriftIssue): string {
  const where = i.column ? `${i.table}.${i.column}` : i.table;
  switch (i.kind) {
    case "undocumented_table":
      return `Table ${where} is not documented`;
    case "undocumented_column":
      return `Column ${where} is not documented`;
    case "unimplemented_table":
      return i.table.startsWith("Unnamed table")
        ? `A documented ${i.table.replace("Unnamed table", "unnamed table")} matches no actual table`
        : `Table ${where} is documented but does not exist`;
    case "unimplemented_column":
      return `Column ${where} is documented but does not exist`;
    case "enum_values":
      return [
        `Values of ${where} differ`,
        i.actual_only?.length ? `undocumented: ${i.actual_only.join(", ")}` : "",
        i.documented_only?.length ? `documented only: ${i.documented_only.join(", ")}` : "",
      ]
        .filter(Boolean)
        .join("; ");
  }
}

function DriftView({ drift }: { drift: SchemaDrift }) {
  const issues = [...drift.issues].sort((a, b) => Number(!!b.new) - Number(!!a.new));
  const doc = drift.documents.map((d) => d.split("/").pop()).join(", ") || "the documentation";
  return (
    <section className="rd-drift-section">
      <h2 className="rd-h2">Documentation drift</h2>
      <p className="muted small">
        Where {doc} disagrees with the actual schema above (tables, columns and enum values; not types). <strong>New</strong> marks mismatches
        this change introduces.
      </p>
      {issues.length === 0 ? (
        <p className="small">The documented schema matches the actual schema.</p>
      ) : (
        <ul className="rd-drift-list small">
          {issues.map((i, n) => (
            <li key={n}>
              {i.new && <span className="rd-flag rd-drift-new">New</span>}
              {driftText(i)}
            </li>
          ))}
        </ul>
      )}
      {drift.resolved.length > 0 && (
        <details className="small">
          <summary>
            {drift.resolved.length} mismatch{drift.resolved.length === 1 ? "" : "es"} resolved by this change
          </summary>
          <ul className="rd-drift-list">
            {drift.resolved.map((i, n) => (
              <li key={n} className="muted">
                {driftText(i)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

// ------------------------------------------------------------------- files ---

function FilesView({ files, source }: { files: { status: string; path: string }[]; source: RichDiffSource }) {
  const { data, isLoading } = useQuery({ queryKey: ["rich-diff-patch"], queryFn: source.loadPatch });
  const label: Record<string, string> = { A: "added", M: "modified", D: "deleted" };
  return (
    <div className="rd-stack">
      <ul className="rd-files">
        {files.map((f) => (
          <li key={f.path}>
            <span className={cx("rd-flag", f.status === "A" ? "rd-added" : f.status === "D" ? "rd-removed" : "rd-modified")}>{label[f.status] ?? f.status}</span>
            <code className="rd-path">{f.path}</code>
          </li>
        ))}
      </ul>
      {isLoading ? <Spinner /> : data ? <DiffView diff={data} /> : <Empty>No diff available.</Empty>}
    </div>
  );
}

// ---------------------------------------------------------------- document ---

function DocPanel({
  a,
  source,
  target,
  item,
  onClose,
}: {
  a: RichDiffAnalysis;
  source: RichDiffSource;
  target: DocTarget;
  item?: TraceItem;
  onClose: () => void;
}) {
  // An item opens on its changes; "Document" shows the whole document after the
  // change (before it, for a removed item or document), at the item.
  const [mode, setMode] = useState<"changes" | "document">(target.id ? "changes" : "document");
  const file = (sha: string) => ({
    queryKey: ["rich-diff-file", sha, target.path],
    queryFn: () => source.loadFile(sha, target.path),
    retry: false,
  });
  const base = useQuery(file(a.base.sha));
  const head = useQuery(file(a.head.sha));
  const useBase = item?.status === "removed" || (head.data === null && base.data != null);
  const content = useBase ? base.data : head.data;
  const isLoading = base.isLoading || head.isLoading;

  const body = useRef<HTMLDivElement>(null);
  // Scroll to the item's heading and mark it.
  useLayoutEffect(() => {
    const root = body.current;
    if (!root || mode !== "document" || content == null || !target.id) return;
    root.querySelectorAll(".rd-hit").forEach((e) => e.classList.remove("rd-hit"));
    const h = [...root.querySelectorAll("h1, h2, h3, h4, h5, h6")].find((e) => {
      const t = (e.textContent ?? "").trim();
      return t === target.id || t.startsWith(`${target.id} `);
    });
    if (h) {
      h.classList.add("rd-hit");
      h.scrollIntoView({ block: "start" });
    } else root.scrollTop = 0;
  }, [content, target.id, mode]);

  return (
    <aside className="rd-doc" aria-label="Document">
      <header className="rd-doc-head">
        <div className="rd-doc-title">
          <Icon name="file" size={14} /> <code>{target.path}</code>
          {target.id && <span className="muted"> · {target.id}</span>}
        </div>
        <div className="rd-doc-actions">
          {target.id && (
            <div className="rd-seg" role="group" aria-label="View">
              <button className={cx(mode === "changes" && "on")} onClick={() => setMode("changes")} title={`${target.id} at ${a.base.rev} against ${a.head.rev}`}>
                Changes
              </button>
              <button className={cx(mode === "document" && "on")} onClick={() => setMode("document")} title="The whole document, at the item">
                Document
              </button>
            </div>
          )}
          <button className="btn-icon" onClick={onClose} aria-label="Close document" title="Close">
            <Icon name="x" />
          </button>
        </div>
      </header>
      <div className="rd-doc-body" ref={body}>
        {isLoading && <Spinner />}
        {!isLoading && mode === "changes" && <ItemChanges id={target.id} before={base.data ?? null} after={head.data ?? null} />}
        {!isLoading && mode === "document" && (
          <>
            {useBase && <p className="muted small">{item?.status === "removed" ? "Removed by this change; shown as it was before." : "Shown as it was before the change."}</p>}
            {content == null ? (
              <p className="muted">This document does not exist at either revision.</p>
            ) : (
              <Markdown className="rd-doc-md">{content.replace(/^\s*<empty-block\/>\s*$/gm, "")}</Markdown>
            )}
          </>
        )}
      </div>
    </aside>
  );
}

/** One item's markdown, before against after. */
function ItemChanges({ id, before, after }: { id: string; before: string | null; after: string | null }) {
  const b = before == null ? null : itemSection(before, id);
  const h = after == null ? null : itemSection(after, id);
  if (b == null && h == null) return <p className="muted">{id} is not in this document at either revision.</p>;
  return (
    <>
      <p className="muted small rd-diff-note">
        {b == null ? "Added by this change." : h == null ? "Removed by this change." : b === h ? "This item's text did not change." : "Deleted text is struck through in red; added text is green."}
      </p>
      <TextDiff before={b ?? ""} after={h ?? ""} context={b != null && h != null ? 3 : undefined} />
    </>
  );
}

/**
 * A line diff with changed words highlighted. `context` collapses unchanged
 * runs to that many lines around the changes (expandable).
 */
function TextDiff({ before, after, context }: { before: string; after: string; context?: number }) {
  const rows = useMemo(() => lineDiff(before, after), [before, after]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  // Single-line values (a title, a property) read best as one run of words.
  if (!before.includes("\n") && !after.includes("\n")) {
    return (
      <div className="rd-textdiff rd-textdiff-inline">
        <Words parts={before && after ? wordDiff(before, after) : [{ op: before ? "del" : "ins", text: before || after }]} />
      </div>
    );
  }
  const shown: (Row | { kind: "gap"; from: number; n: number })[] = [];
  // The first line (the item's heading) always shows.
  const near = (i: number) => context === undefined || i === 0 || rows.slice(Math.max(0, i - context), i + context + 1).some((r) => r.kind !== "same");
  for (let i = 0; i < rows.length; ) {
    if (near(i) || expanded.has(i)) {
      shown.push(rows[i++]);
      continue;
    }
    let j = i;
    while (j < rows.length && !near(j)) j++;
    shown.push({ kind: "gap", from: i, n: j - i });
    i = j;
  }
  return (
    <div className="rd-textdiff">
      {shown.map((r, k) =>
        r.kind === "gap" ? (
          <button
            key={k}
            className="rd-td-gap link-button"
            onClick={() => setExpanded(new Set([...expanded, ...Array.from({ length: r.n }, (_, x) => r.from + x)]))}
          >
            ⋯ {r.n} unchanged line{r.n === 1 ? "" : "s"}
          </button>
        ) : (
          <div key={k} className={cx("rd-td-line", `rd-td-${r.kind}`, r.kind !== "mod" && /^#{1,6}\s/.test(r.text) && "rd-td-heading")}>
            <span className="rd-td-mark" aria-hidden="true">
              {r.kind === "del" ? "−" : r.kind === "ins" ? "+" : r.kind === "mod" ? "~" : ""}
            </span>
            <span className="rd-td-text">{r.kind === "mod" ? <Words parts={r.parts} /> : r.text.replace(/^#{1,6}\s+/, "") || "\u00a0"}</span>
          </div>
        ),
      )}
    </div>
  );
}

function Words({ parts }: { parts: Part[] }) {
  return (
    <>
      {parts.map((p, i) =>
        p.op === "eq" ? (
          <span key={i}>{p.text}</span>
        ) : p.op === "del" ? (
          <del key={i} className="rd-wd-del">
            {p.text}
          </del>
        ) : (
          <ins key={i} className="rd-wd-ins">
            {p.text}
          </ins>
        ),
      )}
    </>
  );
}
